<div class="app-kpi-widget">
  <div class="d-flex align-items-center justify-content-between mb-2">
    <div>
      <p class="heading m-0">App KPI dashboard</p>
      <div class="widget-subtitle">Select brand and app build to inspect KPI metrics.</div>
    </div>
    <div class="d-flex align-items-center">
      <div class="btn-group btn-group-sm view-toggle mr-2" role="group" aria-label="KPI view toggle">
        <button
          type="button"
          class="btn"
          [ngClass]="viewMode === 'gauge' ? 'btn-primary' : 'btn-outline-primary'"
          (click)="setViewMode('gauge')"
        >
          Gauge View
        </button>
        <button
          type="button"
          class="btn"
          [ngClass]="viewMode === 'trend' ? 'btn-primary' : 'btn-outline-primary'"
          (click)="setViewMode('trend')"
        >
          Trend View
        </button>
      </div>
      <button
        type="button"
        class="btn btn-sm btn-outline-primary"
        [disabled]="loading"
        (click)="refresh()"
      >
        {{ loading ? 'Refreshing...' : 'Refresh' }}
      </button>
    </div>
  </div>

  <div class="row no-gutters mb-2">
    <div class="col-md-3 pr-2">
      <label class="filter-label">Brand</label>
      <select class="form-control form-control-sm" [(ngModel)]="selectedBrandName" (change)="onBrandChange()">
        <option value="" disabled>Select brand</option>
        <option *ngFor="let brand of brandOptions" [value]="brand">{{ brand }}</option>
      </select>
    </div>

    <div class="col-md-3 pr-2">
      <label class="filter-label">Product</label>
      <select class="form-control form-control-sm" [(ngModel)]="selectedProjectName" (change)="onProjectChange()">
        <option value="" disabled>Select product</option>
        <option *ngFor="let project of projectOptions" [value]="project">{{ project }}</option>
      </select>
    </div>

    <div class="col-md-3 pr-2" *ngIf="viewMode === 'gauge'">
      <label class="filter-label">App Build</label>
      <select class="form-control form-control-sm" [(ngModel)]="selectedBuildNo" (change)="onBuildChange()">
        <option value="" disabled>Select app build</option>
        <option *ngFor="let build of buildOptions" [value]="build">{{ build }}</option>
      </select>
    </div>

    <div class="col-md-3 pr-2 trend-note-inline" *ngIf="viewMode === 'trend'">
      <label class="filter-label">Build Scope</label>
      <div class="trend-inline-value">Historical: all builds</div>
    </div>

    <div class="col-md-3">
      <label class="filter-label">Platform</label>
      <select class="form-control form-control-sm" [(ngModel)]="selectedPlatformName" (change)="onPlatformChange()">
        <option value="">All</option>
        <option *ngFor="let platform of platformOptions" [value]="platform">{{ platform }}</option>
      </select>
    </div>
  </div>

  <div class="error-msg mb-2" *ngIf="errorMessage">{{ errorMessage }}</div>

  <div class="summary-strip mb-2" *ngIf="gaugeCards.length > 0">
    <span class="summary-badge summary-total">Total: {{ summary.total }}</span>
    <span class="summary-badge summary-pass">Pass: {{ summary.pass }}</span>
    <span class="summary-badge summary-fail">Fail: {{ summary.fail }}</span>
    <span class="summary-badge summary-unknown">Unknown: {{ summary.unknown }}</span>
    <span class="summary-updated" *ngIf="summary.lastUpdated">
      Updated: {{ summary.lastUpdated }}
    </span>
  </div>

  <div class="row no-gutters mb-2" *ngIf="viewMode === 'trend'">
    <div class="col-md-6 pr-2">
      <label class="filter-label">Trend Metric</label>
      <select class="form-control form-control-sm" [(ngModel)]="selectedTrendMetric" (change)="onTrendMetricChange()">
        <option value="" disabled>Select metric</option>
        <option *ngFor="let metric of trendMetricOptions" [value]="metric">{{ metric }}</option>
      </select>
    </div>
    <div class="col-md-6 trend-note">
      Trend plots values across all builds for selected brand/platform.
    </div>
  </div>

  <div class="loading-msg" *ngIf="loading">Loading KPI metrics...</div>

  <div class="row" *ngIf="viewMode === 'gauge' && !loading && gaugeCards.length > 0">
    <div class="col-md-3 col-sm-6 mb-3" *ngFor="let card of gaugeCards">
      <div class="kpi-card">
        <div class="kpi-title" [title]="card.label">{{ card.label }}</div>

        <div
          class="kpi-gauge"
          [style.--gauge-color]="getGaugeColor(card.status)"
        >
          <svg class="kpi-gauge-svg" viewBox="0 0 140 96" aria-hidden="true">
            <path
              class="kpi-gauge-track"
              d="M 16 70 A 54 54 0 0 1 124 70"
            ></path>
            <path
              class="kpi-gauge-progress"
              d="M 16 70 A 54 54 0 0 1 124 70"
              [attr.stroke]="getGaugeColor(card.status)"
              [attr.stroke-dasharray]="getGaugeArcLength(card.score) + ' ' + gaugeSemiCircumference"
            >
              <title>{{ getGaugeTooltip(card) }}</title>
            </path>
            <circle
              class="kpi-gauge-marker"
              [attr.cx]="getGaugeMarkerX(card.score)"
              [attr.cy]="getGaugeMarkerY(card.score)"
              r="4"
              [attr.fill]="getGaugeColor(card.status)"
            >
              <title>Avg: {{ formatMetricValue(card.avgValue) }} {{ card.unit }}</title>
            </circle>
            <text class="kpi-gauge-label kpi-gauge-label-min" x="16" y="90" *ngIf="card.arcLeftLabel">
              {{ card.arcLeftLabel }}: {{ formatMetricValue(card.arcLeftValue) }}
            </text>
            <text class="kpi-gauge-label kpi-gauge-label-max" x="124" y="90" *ngIf="card.arcRightLabel">
              {{ card.arcRightLabel }}: {{ formatMetricValue(card.arcRightValue) }}
            </text>
          </svg>
          <div class="kpi-gauge-inner">
            <div class="kpi-status" [ngClass]="getStatusClass(card.status)">
              {{ card.status || 'N/A' }}
            </div>
            <div class="kpi-value-inside" [title]="getGaugeResultText(card)">
              {{ getGaugeResultText(card) }}
            </div>
          </div>
        </div>

        <div class="kpi-meta-row" *ngFor="let stat of card.displayStats">
          <span>{{ stat.label }}</span>
          <span>{{ formatMetricValue(stat.value) }} {{ card.unit }}</span>
        </div>
        <div class="kpi-meta-row">
          <span>Expected</span>
          <span>{{ card.expectedResult }}</span>
        </div>
        <div class="kpi-meta-row">
          <span>Samples</span>
          <span>{{ card.sampleCount }}</span>
        </div>
      </div>
    </div>
  </div>

  <div class="trend-container" *ngIf="viewMode === 'trend' && !loading && trendSeries.length > 0">
    <highcharts-chart
      [Highcharts]="highcharts"
      [options]="trendChartOptions"
      [oneToOne]="true"
      [(update)]="updateTrendChart"
      style="width: 100%; height: 300px; display: block;"
    ></highcharts-chart>

    <div class="table-responsive mt-2">
      <table class="table table-sm table-bordered table-striped m-0 trend-table">
        <thead>
          <tr>
            <th>Series</th>
            <th>Latest</th>
            <th>Min</th>
            <th>Max</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let row of trendTableRows">
            <td>{{ row.parameter }}</td>
            <td>{{ row.latest }}</td>
            <td>{{ row.min }}</td>
            <td>{{ row.max }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <div class="no-record" *ngIf="viewMode === 'gauge' && !loading && !errorMessage && gaugeCards.length === 0">
    No KPI metrics found for selected brand and app build.
  </div>

  <div class="no-record" *ngIf="viewMode === 'trend' && !loading && !errorMessage && trendSeries.length === 0">
    No trend points available for selected metric and filters.
  </div>
</div>
